import IPassengerPhonNumberViewModel from '@/components/Passenger/models/IPassengerPhoneNumberViewModel';
import Passenger from '@/components/Passenger/models/Passenger';
import {
  IEntity,
  IEntityImporter,
  IEntityMapping,
  IExcelRow,
  IPropertyUpdate
} from '@/typings/import';
import { EEntityMatchType } from '../../enums';
import PropertyUpdateHelper from '../PropertyUpdateHelper';
import PhoneNumberEntity from './PhoneNumberEntity';
import PhoneNumberMapping from './PhoneNumberMapping';

export interface IPhoneNumberImporterParams {
  passengerEntities: Array<IEntity<Passenger>>;
}

export interface IPhoneNumberImporterPostParams {
  passenger: Passenger;
}

export default class PhoneNumberImporter
  implements IEntityImporter<IPassengerPhonNumberViewModel> {
  data: IExcelRow[];
  mapping: IEntityMapping<IPassengerPhonNumberViewModel>;
  entities: Array<IEntity<IPassengerPhonNumberViewModel>>;

  constructor(data: IExcelRow[], mapping: IEntityMapping<IPassengerPhonNumberViewModel>) {
    this.data = data;
    this.mapping = mapping;
    this.entities = this.data.map((d, i) => new PhoneNumberEntity(d, i, this.mapping));
  }

  async matchExisting(row: number, params?: IPhoneNumberImporterParams): Promise<IPassengerPhonNumberViewModel> {
    const entities = this.entities.filter(e => e.excelRowIndex === row);
    let updatedEntities = [];
    if (params && params.passengerEntities) {
      entities.forEach(e => {
        const matchedEntities = this.matchFromPassengers(e, params.passengerEntities, row);
        const first = matchedEntities.shift();
        e.entity = first.entity;
        e.matchedBy = first.matchedBy;
        e.propertyUpdates = first.propertyUpdates;
        updatedEntities = updatedEntities.concat(matchedEntities);
      });
    }

    this.entities = this.entities.concat(updatedEntities);
    return null;
  }

  async postEntity(
    entity: IEntity<IPassengerPhonNumberViewModel>, 
    params?: any, 
  ): Promise<IPassengerPhonNumberViewModel> {
    const phoneNumber = entity.entity;
    if (!phoneNumber) {
      return null;
    }

    const doNotUpdateProperties = PhoneNumberMapping.NON_ENTITY_MAPPINGS;

    entity.propertyUpdates
    .filter(pu => !pu.doNotUpdate && !doNotUpdateProperties.includes(pu.propertyName))
    .filter(pu => this.shouldUpdateProperty(pu, entity.matchedBy))
    .forEach(pu => phoneNumber[pu.propertyName] = PropertyUpdateHelper.getValue(pu));

    return Promise.resolve(phoneNumber);
  }

  onMappingChanged(mapping: IEntityMapping<IPassengerPhonNumberViewModel>) {
    this.mapping = mapping;
    this.entities.forEach(e => e.onMappingChanged(mapping, this.data[e.excelRowIndex]));
  }

  private matchFromPassengers(
    entity: IEntity<IPassengerPhonNumberViewModel>, 
    passengerEntities: Array<IEntity<Passenger>>,
    rowIndex: number,
  ): Array<IEntity<IPassengerPhonNumberViewModel>> {

    const numberUpdate = entity.propertyUpdates.find(pu => pu.propertyName === 'number');
    if (!numberUpdate) {
      return null;
    }

    const numberValue = PropertyUpdateHelper.getNewValueAsPropertyValue<string>(numberUpdate);

    const matchedPassengers = passengerEntities
      .filter(pe => pe.entity && pe.entity.phoneNumbers.some(pn => pn.number === numberValue));

    if (matchedPassengers.length === 0) {
      if (this.shouldCreateEntity(entity)) {
        const phoneNumber = this.createNewPhoneNumber(entity.propertyUpdates);
        entity.entity = phoneNumber;
      }
      entity.matchedBy = EEntityMatchType.Created;
      return [ entity ];
    }
    const matchedEntities = [];
    matchedPassengers.forEach(mp => {
      const match = mp.entity.phoneNumbers.find(pn => pn.number === numberValue);
      if (match) {
        const createdEntity = new PhoneNumberEntity(this.data[rowIndex], rowIndex, this.mapping);
        createdEntity.entity = match;
        createdEntity.matchedBy = mp.matchedBy;
        createdEntity.propertyUpdates.forEach(pu => pu.oldValue = createdEntity.entity[pu.propertyName] || null);
        matchedEntities.push(createdEntity); 
      }
    });

    return matchedEntities;
  }

  private createNewPhoneNumber(propertyUpdates: Array<IPropertyUpdate<IPassengerPhonNumberViewModel>>) {
    const phoneNumberProps = propertyUpdates
      .filter(pu => !PhoneNumberMapping.NON_ENTITY_MAPPINGS.includes(pu.propertyName));
    
    const phoneNumber: IPassengerPhonNumberViewModel = {
      id: 0,
      passengerId: 0,
      number: '',
      sms: false,
      showToDriver: false,
      assistant: false,
    };

    phoneNumberProps.forEach(p => phoneNumber[p.propertyName] = p.newValue);

    return phoneNumber;
  }

  private isCreationColumnDefined() {
    const mappingProperty = this.mapping.props
    .find(p => p.propertyName === PhoneNumberMapping.ENTITY_CREATION_MAPPING);
      
    return mappingProperty && mappingProperty.cell;
  }

  private shouldCreateEntity(entity: IEntity<IPassengerPhonNumberViewModel>) {
    if (!this.isCreationColumnDefined())  {
      return true;
    }

    const creationPropertyUpdate = entity.propertyUpdates
    .find(pu => pu.propertyName === PhoneNumberMapping.ENTITY_CREATION_MAPPING);

    if (!creationPropertyUpdate) {
      return false;
    }

    const value = PropertyUpdateHelper.getNewValueAsPropertyValue<string>(creationPropertyUpdate);
    if (value) {
      return true;
    }

    return false;
  }

  private shouldUpdateProperty(propertyUpdate: IPropertyUpdate<any>, matchedBy: EEntityMatchType) {
    if (matchedBy === EEntityMatchType.Created) {
      return true;
    }
    // Keep matched original values if no mapping has been given
    const mappingProperty = this.mapping.props.find(p => p.propertyName === propertyUpdate.propertyName);
    if (!mappingProperty) {
      return false;
    }
    return PropertyUpdateHelper.propertyMappingIsDefined(mappingProperty);
  }

}
